import { inject, Injectable } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { ImageLoadingStrategy } from '@spartacus/storefront';

/**
 * Service for managing loading strategies and preloading for interactive carousel items.
 *
 * Features:
 * - Maintains list of eager item UIDs
 * - Provides loading strategy based on eager list
 * - Handles preloading of eager items
 * - Device-specific image preloading (mobile images on mobile, desktop images on desktop)
 * - SSR-safe implementation
 *
 * Usage Example:
 * ```typescript
 * // In main component ngOnInit:
 * this.loadingStrategyService.reset();
 * this.loadingStrategyService.setEagerItems(['item1', 'item2']);
 *
 * // In item component:
 * const strategy = this.loadingStrategyService.getLoadingStrategy(itemData);
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class InteractiveCarouselLoadingStrategyService {
  private eagerItemUids = new Set<string>();
  private winRef = inject(WindowRef);

  setEagerItems(itemUids: string[]): void {
    this.eagerItemUids = new Set(itemUids);
  }

  /**
   * Determines the loading strategy ('eager' or 'lazy') for a slider item
   * if item is on eager list is it also preloaded
   * @param itemUid The unique identifier of the slider item
   * @returns 'eager' if item is in eager list, 'lazy' otherwise
   */
  getLoadingStrategy(itemData: any): ImageLoadingStrategy {
    const isEager = this.eagerItemUids.has(itemData.uid);

    if (isEager) {
      this.preloadItem(itemData);
    }

    return isEager ? ImageLoadingStrategy.EAGER : ImageLoadingStrategy.LAZY;
  }

  /**
   * Preload images for a specific item based on device type
   * @param itemData The item data containing image information
   */
  preloadItem(itemData: any): void {
    if (!this.winRef?.nativeWindow) {
      return;
    }

    const isMobile = this.isMobileDevice();
    const imageUrl = isMobile ? itemData.mobileImage?.url : itemData.image?.url;

    if (imageUrl) {
      // console.log(`Preloading image: ${imageUrl}`);
      this.createImagePreload(imageUrl);
    }
  }

  /**
   * Detect if the current device is mobile using user agent
   */
  private isMobileDevice(): boolean {
    if (!this.winRef?.nativeWindow?.navigator) {
      return false;
    }

    const userAgent = this.winRef.nativeWindow.navigator.userAgent;
    const mobileRegex = /(iPhone|iPod|Android|webOS|SamsungBrowser|Mobi)/i;
    return mobileRegex.test(userAgent);
  }

  /**
   * Create an image preload element
   */
  private createImagePreload(imageUrl: string): void {
    if (!this.winRef?.document) {
      return;
    }

    // Check if preload link already exists to avoid duplicates
    const existingPreload = this.winRef.document.querySelector(`link[rel="preload"][href="${imageUrl}"]`);
    if (existingPreload) {
      return;
    }

    // Create preload link element
    const linkElement = this.winRef.document.createElement('link');
    linkElement.setAttribute('rel', 'preload');
    linkElement.setAttribute('as', 'image');
    linkElement.setAttribute('href', imageUrl);
    // Add to document head
    const head = this.winRef.document.head || this.winRef.document.getElementsByTagName('head')[0];
    if (head) {
      head.appendChild(linkElement);
    }
  }

  reset(): void {
    this.eagerItemUids.clear();
  }
}
